import { Camera } from "../render/Camera";
import type { Demo, Scene, SceneAction } from "./types";
import { makeWorld, box, ball, polygon, rng } from "./helpers";
import type { Body, CollisionEvent } from "fast-2d";
import { collisionImpactPoint, fractureBody } from "./fracture";

const IMPACT_SPEED = 10.5;

export const breakableWall: Demo = {
	id: "breakable-wall",
	title: "Breakable wall",
	blurb:
		"A dense brick wall is built from individual bodies, then a heavy boulder tears through it and leaves a tumbling debris field.",
	category: "Action & Destruction",
	build(): Scene {
		const world = makeWorld();
		box(world, 0, 5.25, 28, 0.9, { isStatic: true, friction: 0.9 });
		box(world, -13.8, -0.8, 0.8, 12.2, { isStatic: true, friction: 0.8 });
		box(world, 13.8, -0.8, 0.8, 12.2, { isStatic: true, friction: 0.8 });
		box(world, 0, -6.9, 28, 0.8, { isStatic: true, friction: 0.7 });

		const random = rng(0xb4eaab1e);
		const breakables = new Set<Body>();
		const generations = new Map<Body, number>();
		const pending: { target: Body; point: [number, number]; strength: number }[] = [];
		const brickW = 0.82;
		const brickH = 0.42;
		const rows = 10;
		const cols = 8;
		for (let row = 0; row < rows; row++) {
			const offset = row % 2 === 0 ? 0 : brickW * 0.5;
			for (let col = 0; col < cols; col++) {
				const x = 3.0 + (col - (cols - 1) / 2) * (brickW + 0.015) + offset;
				const y = 4.75 - brickH / 2 - row * (brickH + 0.012);
				const bevel = 0.06 + (row % 3) * 0.012;
				const topSkew = (col % 2 === 0 ? -1 : 1) * 0.035;
				const brick = polygon(world, x, y, [
					[-brickW / 2 + bevel, -brickH / 2],
					[brickW / 2 - bevel + topSkew, -brickH / 2],
					[brickW / 2, -brickH / 2 + bevel],
					[brickW / 2 - bevel, brickH / 2],
					[-brickW / 2 + bevel - topSkew, brickH / 2],
					[-brickW / 2, -brickH / 2 + bevel],
				], {
					friction: 0.72,
					restitution: 0.03,
					density: 2.55,
				});
				breakables.add(brick);
				generations.set(brick, 0);
			}
		}

		const boulders: Body[] = [];
		let launched = false;
		let launchDelay = 0.42;

		const launchBoulder = (): void => {
			while (boulders.length >= 3) {
				const old = boulders.shift()!;
				if (world.bodies.includes(old)) world.remove(old);
			}
			boulders.push(
				ball(world, -9.6, 1.1, 0.74, {
					density: 26,
					friction: 0.58,
					restitution: 0.08,
					velocity: [25, -1.0],
					angularVelocity: -8,
				}),
			);
		};

		const onCollision = (event: CollisionEvent): void => {
			const target = breakables.has(event.bodyA) ? event.bodyA : breakables.has(event.bodyB) ? event.bodyB : null;
			if (!target || (generations.get(target) ?? 0) > 0) return;
			const other = target === event.bodyA ? event.bodyB : event.bodyA;
			if (!boulders.includes(other)) return;
			const dx = other.velocity.x - target.velocity.x;
			const dy = other.velocity.y - target.velocity.y;
			const strength = Math.hypot(dx, dy);
			if (strength < IMPACT_SPEED || pending.some((item) => item.target === target)) return;
			pending.push({ target, point: collisionImpactPoint(event), strength });
		};

		world.on("collisionStart", onCollision);

		const actions: SceneAction[] = [{ label: "Launch boulder", run: launchBoulder }];

		return {
			world,
			camera: new Camera(0, -1.1, 31),
			hint: "Drag debris or launch another boulder",
			actions: () => actions,
			onBeforeStep(dt): void {
				while (pending.length > 0) {
					const event = pending.shift()!;
					if (!world.bodies.includes(event.target)) continue;
					breakables.delete(event.target);
					const shards = fractureBody(world, event.target, event.point, event.strength, {
						rng: random,
						maxFragments: 4,
						minArea: 0.025,
						minEdge: 0.1,
						impulseScale: 0.055,
						maxShardSpeed: 18,
						densityScale: 0.86,
					});
					for (const shard of shards) generations.set(shard, 1);
				}
				if (launched) return;
				launchDelay -= dt;
				if (launchDelay <= 0) {
					launched = true;
					launchBoulder();
				}
			},
		};
	},
};